import React from 'react'
import { useNavigate } from 'react-router-dom'

import hiredCoachLogo from '@/assets/hiredcoach-logo.svg'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Drawer, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle } from '@/components/ui/drawer'
import { useIsMobile } from '@/hooks/use-mobile'
import { cn } from '@/lib/utils'

export interface PremiumLockModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  featureLabel?: string
  description?: string
  ctaLabel?: string
  onSubscribeClick?: () => void
  className?: string
}

/**
 * PremiumLockModal
 *
 * A reusable, mobile-friendly modal that informs the user a feature is for Premium members.
 * Uses Dialog on desktop and Drawer on mobile for a natural, native feel.
 *
 * Usage:
 * <PremiumLockModal open={open} onOpenChange={setOpen} featureLabel="Interview Grading" />
 */
export const PremiumLockModal: React.FC<PremiumLockModalProps> = ({
  open,
  onOpenChange,
  featureLabel,
  description,
  ctaLabel = 'Subscribe now',
  onSubscribeClick,
  className,
}) => {
  const isMobile = useIsMobile()
  const navigate = useNavigate()

  const title = featureLabel ? `${featureLabel} is a premium feature` : 'Premium feature'
  const desc =
    description ||
    "Unlock this feature and more with HiredCoach Premium. Get unlimited practice, detailed feedback, and pro tools to accelerate your job hunt."

  const handleSubscribe = () => {
    onOpenChange(false)
    if (onSubscribeClick) {
      onSubscribeClick()
    } else {
      navigate('/subscription')
    }
  }

  const Illustration = (
    <div className="flex items-center gap-3">
      <img src={hiredCoachLogo} alt="HiredCoach" className="h-10 w-10" />
      <div className="flex flex-col">
        <span className="text-base font-semibold text-blue-900">Unlock with Premium</span>
        <span className="text-xs text-blue-700">Your AI coach, without limits</span>
      </div>
    </div>
  )

  const Body = (
    <div className="mt-4 space-y-3">
      <p className="text-sm text-slate-700">{desc}</p>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-slate-600">
        <li>Unlimited interviews and pitch practice</li>
        <li>Detailed AI feedback and grading</li>
        <li>Resume insights and tailored questions</li>
      </ul>
      <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-900">
        <div className="text-xs font-medium">This action is available to Premium members.</div>
      </div>
    </div>
  )

  const Actions = (
    <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-end">
      <Button
        variant="secondary"
        className="w-full sm:w-auto"
        onClick={() => onOpenChange(false)}
      >
        Not now
      </Button>
      {/* Use Link for standard navigation when opened from static UI, but default handler uses navigate */}
      <Button className="w-full sm:w-auto bg-blue-600 text-white hover:bg-blue-700" onClick={handleSubscribe}>
        {ctaLabel}
      </Button>
    </div>
  )

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className={cn('px-4', className)}>
          <div className="mx-auto my-2 h-1 w-12 rounded-full bg-slate-300" />
          <DrawerHeader className="text-left">
            {Illustration}
            <DrawerTitle className="mt-3 text-lg">{title}</DrawerTitle>
            <DrawerDescription className="text-sm text-slate-600">
              Premium access required
            </DrawerDescription>
          </DrawerHeader>
          <div className="px-4 pb-4 pt-0">
            {Body}
            <DrawerFooter className="px-0 pt-4">
              {Actions}
            </DrawerFooter>
          </div>
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn('sm:max-w-md', className)}>
        <DialogHeader>
          {Illustration}
          <DialogTitle className="mt-2 text-xl">{title}</DialogTitle>
          <DialogDescription>Premium access required</DialogDescription>
        </DialogHeader>
        {Body}
        <DialogFooter className="mt-4">
          {Actions}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default PremiumLockModal
